import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { io } from 'socket.io-client';

import config from '@/config';

// Timing windows that govern the auction stream health checks and badge states.
const KEEP_ALIVE_INTERVAL_MS = 15000;
const STALE_THRESHOLD_MS = KEEP_ALIVE_INTERVAL_MS * 2;
const RESUMED_BADGE_DURATION_MS = 4000;

const apiRealtimeConfig = config?.apis?.api ?? {};
const SOCKET_EVENT_NAME = apiRealtimeConfig.socketEventName ?? 'auction:event';
const SOCKET_BACKLOG_EVENT = apiRealtimeConfig.socketBacklogEvent ?? 'auction:backlog';
const SOCKET_SUBSCRIBE_EVENT = apiRealtimeConfig.socketSubscribeEvent ?? 'artwork:subscribe';
const SOCKET_UNSUBSCRIBE_EVENT = apiRealtimeConfig.socketUnsubscribeEvent ?? 'artwork:unsubscribe';

// Derive the best websocket URL/path combination from the runtime configuration.
const resolveSocketConfig = () => {
  const apiConfig = config?.apis?.api ?? {};

  if (!apiConfig) {
    return { wsUrl: null, socketPath: '/socket.io' };
  }

  let wsUrl = null;

  if (apiConfig.wsUrl) {
    wsUrl = apiConfig.wsUrl;
  } else if (apiConfig.url) {
    try {
      const parsed = new URL(apiConfig.url);
      const scheme = parsed.protocol === 'https:' ? 'wss:' : parsed.protocol === 'http:' ? 'ws:' : parsed.protocol;
      wsUrl = `${scheme}//${parsed.host}`;
    } catch (error) {
      const protocol = apiConfig.protocol === 'https' ? 'wss' : 'ws';
      const port = apiConfig.port ? `:${apiConfig.port}` : '';
      wsUrl = apiConfig.host ? `${protocol}://${apiConfig.host}${port}` : null;
    }
  } else if (apiConfig.host) {
    const protocol = apiConfig.protocol === 'https' ? 'wss' : 'ws';
    const port = apiConfig.port ? `:${apiConfig.port}` : '';
    wsUrl = `${protocol}://${apiConfig.host}${port}`;
  }

  const socketPath =
    apiConfig.socketPath
    ?? process.env.REACT_APP_SOCKET_PATH
    ?? process.env.BACKEND_SOCKET_PATH
    ?? '/socket.io';

  return { wsUrl, socketPath };
};

// Exported helpers used by consumers to normalize bid identities across payload variations.
export const deriveBidIdentifier = (bid) => bid?.id || bid?._id || bid?.bid_id || null;

// Normalize auction bids from the server so downstream components see a consistent shape.
export const normalizeAuctionBid = (rawBid) => {
  if (!rawBid || typeof rawBid !== 'object') {
    return null;
  }

  const bidder = (rawBid.bidder && typeof rawBid.bidder === 'object') ? rawBid.bidder : null;
  const bidderId =
    rawBid.bidder_id ||
    rawBid.bidderId ||
    bidder?._id ||
    bidder?.id ||
    bidder?.user_id ||
    null;
  const bidderImageId =
    rawBid.bidder_image_id ||
    rawBid.bidder_image ||
    bidder?.user_image?.image_id ||
    bidder?.gallery_image?.image_id ||
    bidder?.artist_image?.image_id ||
    null;
  const bidderType =
    rawBid.bidder_type ||
    bidder?.user_type ||
    bidder?.type ||
    null;
  const bidderUsername =
    rawBid.bidder_username ||
    rawBid.bidderUsername ||
    bidder?.user_username ||
    bidder?.username ||
    null;
  const timestampCandidate =
    rawBid.created_at ||
    rawBid.placed_at ||
    rawBid.timestamp ||
    null;
  let normalizedTimestamp = timestampCandidate;

  if (timestampCandidate) {
    const parsed = new Date(timestampCandidate);
    if (!Number.isFinite(parsed.getTime())) {
      normalizedTimestamp = null;
    } else {
      normalizedTimestamp = parsed.toISOString();
    }
  }

  if (!normalizedTimestamp) {
    normalizedTimestamp = new Date().toISOString();
  }

  const identifier = deriveBidIdentifier(rawBid);
  const idempotencyKey = rawBid.idempotency_key || rawBid.idempotencyKey || null;

  let normalizedBidder = bidder
    ? {
      ...bidder,
      _id: bidder._id || bidderId || bidder?.id || null,
      id: bidder.id || bidderId || bidder?._id || null,
      user_id: bidder.user_id || bidderId || null,
      user_username: bidder.user_username || bidderUsername || null,
      username: bidder.username || bidderUsername || null,
      bidder_username: bidder.bidder_username || bidderUsername || null,
      user_type: bidder.user_type || bidderType || null,
      bidder_type: bidder.bidder_type || bidderType || null,
    }
    : null;

  if (!normalizedBidder && (bidderId || bidderUsername || bidderType)) {
    normalizedBidder = {
      _id: bidderId || null,
      id: bidderId || null,
      user_id: bidderId || null,
      user_username: bidderUsername || null,
      username: bidderUsername || null,
      bidder_username: bidderUsername || null,
      user_type: bidderType || null,
      bidder_type: bidderType || null,
    };
  }

  return {
    ...rawBid,
    id: identifier || rawBid.id || rawBid._id || rawBid.bid_id || null,
    created_at: normalizedTimestamp,
    placed_at: rawBid.placed_at || normalizedTimestamp,
    bidder_type: bidderType,
    bidder_id: bidderId || rawBid.bidder_id || null,
    bidder_username: bidderUsername || rawBid.bidder_username || null,
    idempotency_key: idempotencyKey,
    idempotencyKey,
    bidder_image_id: bidderImageId,
    bidder: normalizedBidder
      ? {
        ...normalizedBidder,
        user_type: bidderType || normalizedBidder.user_type || null,
        bidder_type: bidderType || normalizedBidder.bidder_type || null,
        user_image: bidderImageId
          ? { ...(normalizedBidder.user_image ?? {}), image_id: bidderImageId }
          : (normalizedBidder.user_image ?? null),
      }
      : null,
  };
};

// Build a short signature string so UI can detect changes to the list without deep compares.
export const buildBidSignature = (bids) => {
  if (!Array.isArray(bids) || bids.length === 0) {
    return `len:${Array.isArray(bids) ? bids.length : 0}`;
  }

  const preview = bids
    .slice(0, 5)
    .map((bid) => {
      const identifier = deriveBidIdentifier(bid) || '';
      const amount = Number.isFinite(Number(bid?.amount)) ? Number(bid.amount) : '';
      const createdAt = bid?.created_at || bid?.placed_at || '';
      return `${identifier}:${amount}:${createdAt}`;
    })
    .join(';');

  return `len:${bids.length}|${preview}`;
};

// Compute jittered backoff for reconnection retries so we don't hammer the server.
const computeBackoffDelay = (attempt) => {
  if (attempt <= 0) {
    return 0;
  }

  const base = Math.min(5000, 250 * (2 ** (attempt - 1)));
  const jitter = Math.random() * 0.25 * base;
  return base + jitter;
};

// Hook that manages the websocket lifecycle for artwork auctions and exposes stream metadata.
export default function useAuctionLiveStream({
  artworkId,
  isAuctionEnabled,
  shouldConnect,
  onEvent,
  userId,
}) {
  const socketRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const lastSequenceRef = useRef(0);
  const reconnectAttemptRef = useRef(0);
  const lastEventAtRef = useRef(null);
  const lastMetaAtRef = useRef(null);

  const [connectionStatus, setConnectionStatus] = useState({
    status: 'idle',
    resumedAt: null,
  });
  const connectionStatusRef = useRef(connectionStatus);
  const [serverTimeOffsetMs, setServerTimeOffsetMs] = useState(0);
  const [isCountdownFrozen, setIsCountdownFrozen] = useState(true);

  // Helpers to manage the scheduled reconnect when the stream drops.
  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  // Gracefully teardown any open socket so we can reinitialize with fresh handlers.
  const disconnectSocket = useCallback(() => {
    const socket = socketRef.current;
    if (!socket) {
      return;
    }

    try {
      if (socket.connected && artworkId) {
        socket.emit(SOCKET_UNSUBSCRIBE_EVENT, { artworkId });
      }
      socket.removeAllListeners();
      socket.disconnect();
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('[ARTWORK_AUCTION][Stream] Failed to cleanup socket connection', {
          artworkId,
          userId,
          error,
        });
      }
    }

    socketRef.current = null;
  }, [artworkId, userId]);

  // Update UI state to show reconnection is in progress while we attempt to rejoin.
  const markDisconnected = useCallback(() => {
    setConnectionStatus((prev) => ({
      status: 'reconnecting',
      resumedAt: prev.resumedAt,
    }));
  }, []);

  // Normalize and relay every event received from the bidding stream.
  const handleEventPayload = useCallback((event) => {
    if (!event || typeof event !== 'object') {
      return;
    }

    const { sequence, type, payload, publishedAt } = event;
    if (!Number.isFinite(sequence) || sequence <= lastSequenceRef.current) {
      return;
    }

    lastSequenceRef.current = sequence;
    lastEventAtRef.current = Date.now();

    if (publishedAt) {
      const publishedMs = new Date(publishedAt).getTime();
      if (Number.isFinite(publishedMs)) {
        setServerTimeOffsetMs(publishedMs - Date.now());
      }
    }

    if (type === 'meta') {
      const serverTime = payload?.serverTime;
      if (serverTime) {
        const serverMs = new Date(serverTime).getTime();
        if (Number.isFinite(serverMs)) {
          setServerTimeOffsetMs(serverMs - Date.now());
        }
      }
      lastMetaAtRef.current = Date.now();
      setIsCountdownFrozen(false);
    } else if (type === 'close' || type === 'bid') {
      lastMetaAtRef.current = Date.now();
      setIsCountdownFrozen(false);
    }

    if (onEvent) {
      onEvent(event);
    }
  }, [onEvent]);

  const shouldConnectRef = useRef(false);
  const canConnect = Boolean(artworkId && shouldConnect && isAuctionEnabled);
  shouldConnectRef.current = canConnect;

  // Kick off the websocket connection, subscribe to the artwork, and wire events.
  const connectStream = useCallback(() => {
    if (!artworkId) {
      return;
    }

    const { wsUrl, socketPath } = resolveSocketConfig();
    if (!wsUrl) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('[ARTWORK_AUCTION][Stream] WebSocket endpoint unavailable', { artworkId });
      }
      setConnectionStatus((prev) => ({
        status: 'idle',
        resumedAt: prev.resumedAt,
      }));
      return;
    }

    clearReconnectTimer();
    disconnectSocket();

    try {
      const socket = io(wsUrl, {
        path: socketPath,
        withCredentials: true,
        transports: ['websocket', 'polling'],
        autoConnect: false,
        reconnection: false,
      });

      socketRef.current = socket;

      // Process backlog payload to catch up with events emitted while we were away.
      const handleBacklog = (payload) => {
        if (!payload) {
          return;
        }

        const events = Array.isArray(payload.events) ? [...payload.events] : [];
        events
          .sort((a, b) => {
            const aSeq = Number(a?.sequence ?? 0);
            const bSeq = Number(b?.sequence ?? 0);
            return aSeq - bSeq;
          })
          .forEach(handleEventPayload);

        const nextSequence = Number(payload.lastSequence);
        if (Number.isFinite(nextSequence) && nextSequence > lastSequenceRef.current) {
          lastSequenceRef.current = nextSequence;
        }
      };

      // Mark the stream as live and request the server to replay any missed events.
      const handleConnected = () => {
        reconnectAttemptRef.current = 0;
        lastEventAtRef.current = Date.now();

        setConnectionStatus((prev) => ({
          status: 'live',
          resumedAt: prev.status === 'live' ? prev.resumedAt : Date.now(),
        }));

        socket.emit(
          SOCKET_SUBSCRIBE_EVENT,
          {
            artworkId,
            sendBacklog: true,
            lastSeenSequence: lastSequenceRef.current || undefined,
            backlogEvent: SOCKET_BACKLOG_EVENT,
          },
          (ack) => {
            if (ack && ack.ok === false && process.env.NODE_ENV !== 'production') {
              console.warn('[ARTWORK_AUCTION][Stream] Subscription rejected', {
                artworkId,
                ack,
              });
            }
          },
        );
      };

      // Schedule a delayed reconnection with exponential backoff to avoid storming the server.
      const scheduleReconnect = () => {
        if (!shouldConnectRef.current) {
          return;
        }

        markDisconnected();
        reconnectAttemptRef.current += 1;
        const delay = computeBackoffDelay(reconnectAttemptRef.current);
        clearReconnectTimer();
        reconnectTimeoutRef.current = setTimeout(() => {
          if (!shouldConnectRef.current) {
            return;
          }
          connectStream();
        }, delay);
      };

      // Log connection issues and immediately trigger the reconnection path.
      const handleConnectError = (error) => {
        if (process.env.NODE_ENV !== 'production') {
          console.warn('[ARTWORK_AUCTION][Stream] Socket connection error', {
            artworkId,
            userId,
            error,
          });
        }
        disconnectSocket();
        scheduleReconnect();
      };

      // When the server drops us, attempt to recover like a network blip.
      const handleDisconnected = (reason) => {
        if (process.env.NODE_ENV !== 'production') {
          console.warn('[ARTWORK_AUCTION][Stream] Socket disconnected', {
            artworkId,
            userId,
            reason,
          });
        }
        disconnectSocket();
        scheduleReconnect();
      };

      socket.on('connect', handleConnected);
      socket.on(SOCKET_EVENT_NAME, handleEventPayload);
      socket.on(SOCKET_BACKLOG_EVENT, handleBacklog);
      socket.on('connect_error', handleConnectError);
      socket.on('error', handleConnectError);
      socket.on('disconnect', handleDisconnected);

      socket.connect();
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('[ARTWORK_AUCTION][Stream] Failed to establish socket connection', {
          artworkId,
          userId,
          error,
        });
      }
      disconnectSocket();
      markDisconnected();
      reconnectAttemptRef.current += 1;
      const delay = computeBackoffDelay(reconnectAttemptRef.current);
      clearReconnectTimer();
      reconnectTimeoutRef.current = setTimeout(() => {
        if (!shouldConnectRef.current) {
          return;
        }
        connectStream();
      }, delay);
    }
  }, [artworkId, userId, clearReconnectTimer, disconnectSocket, handleEventPayload, markDisconnected]);

  useEffect(() => {
    connectionStatusRef.current = connectionStatus;
  }, [connectionStatus]);

  // Toggle the socket connection whenever the readiness flags change.
  useEffect(() => {
    if (!canConnect) {
      disconnectSocket();
      clearReconnectTimer();
      setConnectionStatus((prev) => ({
        status: 'idle',
        resumedAt: prev.resumedAt,
      }));
      return undefined;
    }

    connectStream();

    return () => {
      disconnectSocket();
      clearReconnectTimer();
    };
  }, [canConnect, clearReconnectTimer, connectStream, disconnectSocket]);

  // Keep an eye on the relative time so the countdown UI shows a frozen state when no pings arrive.
  useEffect(() => {
    if (!canConnect) {
      return undefined;
    }

    const interval = setInterval(() => {
      if (!lastMetaAtRef.current) {
        setIsCountdownFrozen(true);
        return;
      }

      const elapsed = Date.now() - lastMetaAtRef.current;
      setIsCountdownFrozen(elapsed > STALE_THRESHOLD_MS);
    }, 2000);

    return () => clearInterval(interval);
  }, [canConnect]);

  // Heartbeat watchdog: reconnect if we haven’t received events within the allowed window.
  useEffect(() => {
    if (!canConnect) {
      return undefined;
    }

    const interval = setInterval(() => {
      if (!shouldConnectRef.current) {
        return;
      }

      if (!lastEventAtRef.current) {
        return;
      }

      const elapsed = Date.now() - lastEventAtRef.current;
      if (elapsed <= STALE_THRESHOLD_MS) {
        return;
      }

      if (connectionStatusRef.current?.status === 'live') {
        if (process.env.NODE_ENV !== 'production') {
          console.warn('[ARTWORK_AUCTION][Stream] Heartbeat missed; forcing reconnect', {
            artworkId,
            userId,
            elapsed,
          });
        }
        markDisconnected();
        disconnectSocket();
        reconnectAttemptRef.current += 1;
        const delay = computeBackoffDelay(reconnectAttemptRef.current);
        clearReconnectTimer();
        reconnectTimeoutRef.current = setTimeout(() => {
          if (!shouldConnectRef.current) {
            return;
          }
          connectStream();
        }, delay);
      }
    }, KEEP_ALIVE_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [canConnect, connectStream, markDisconnected, clearReconnectTimer, disconnectSocket, artworkId, userId]);

  // Automatically hide the “resumed” badge after a short duration.
  useEffect(() => {
    if (connectionStatus.status !== 'live' || !connectionStatus.resumedAt) {
      return undefined;
    }

    const remaining = connectionStatus.resumedAt + RESUMED_BADGE_DURATION_MS - Date.now();
    if (remaining <= 0) {
      setConnectionStatus((prev) => ({
        ...prev,
        resumedAt: null,
      }));
      return undefined;
    }

    const timer = setTimeout(() => {
      setConnectionStatus((prev) => ({
        ...prev,
        resumedAt: null,
      }));
    }, remaining);

    return () => clearTimeout(timer);
  }, [connectionStatus.resumedAt, connectionStatus.status]);

  const connectionBadge = useMemo(() => {
    if (connectionStatus.status === 'live') {
      if (connectionStatus.resumedAt) {
        return { tone: 'resumed', label: 'Live (resumed)' };
      }
      return { tone: 'live', label: 'Live' };
    }
    if (connectionStatus.status === 'reconnecting') {
      return { tone: 'reconnecting', label: 'Reconnecting...' };
    }
    return { tone: 'idle', label: 'Offline' };
  }, [connectionStatus]);

  return {
    connectionBadge,
    isCountdownFrozen,
    serverTimeOffsetMs,
    lastSequence: lastSequenceRef.current,
  };
}
